import { useState, useEffect } from 'preact/hooks';
import { playback } from '../state/store.js';
import {
  playerSettings,
  setRate,
  setAudioStyle,
  PLAYBACK_RATES,
} from '../state/player-settings.js';
import {
  chapterStatus,
  dismissResume,
  gotoNextChapter,
  gotoNextVerse,
  gotoPrevChapter,
  gotoPrevVerse,
  resumeAvailable,
  resumeFromSaved,
  retryLoad,
  seekAbsolute,
  togglePlay,
} from '../audio/controller.js';
import { books, chapterError } from '../state/chapter-store.js';
import { route } from '../state/store.js';
import { colorProfile, setColorProfile, COLOR_PROFILES } from '../state/color-profile.js';
import { currentBible, compareBible, setCompareBible } from '../state/bible-store.js';
import { BiblePicker } from './BiblePicker.js';
import {
  cancelSleepTimer,
  scheduleEndOfChapter,
  sleepRemainingSec,
  sleepTimer,
  SLEEP_PRESETS_MINUTES,
  startSleepTimer,
} from '../state/sleep-timer.js';
import {
  readingPrefs,
  setFontSize,
  setLineSpacing,
  setLiteracyMode,
  setParagraphMode,
  FONT_SIZES,
  LINE_SPACINGS,
  FONT_SIZE_LABELS,
  LINE_SPACING_LABELS,
} from '../state/reading-prefs.js';
import {
  notificationPrefs,
  requestPermissionAndEnable,
  setNotificationHour,
  setNotificationsEnabled,
} from '../state/notifications-store.js';
import { askOpen } from './Ask.js';
import { BadgeInventory } from './BadgeInventory.js';
import { OfflinePrompt, useOfflinePrompt } from './OfflinePrompt.js';
import { DownloadProgressBar } from './DownloadProgressBar.js';
import { earnedBadges, BADGE_CATALOG } from '../state/badges-store.js';
import { freezeTokens, consumeFreezeToken } from '../state/progress-store.js';
import {
  AiSparkleIcon,
  GearIcon,
  PauseIcon,
  PlayIcon,
  ScrubBackIcon,
  ScrubFwdIcon,
  SkipNextIcon,
  SkipPrevIcon,
} from './player-icons.js';

export function PlayerBar() {
  const p = playback.value;
  const status = chapterStatus.value;
  const disabled = status !== 'ready' && status !== 'demo';

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [biblePickerOpen, setBiblePickerOpen] = useState(false);
  const [comparePickerOpen, setComparePickerOpen] = useState(false);
  const [badgeInventoryOpen, setBadgeInventoryOpen] = useState(false);
  const { open: openOfflinePrompt } = useOfflinePrompt();

  const pct = p.durationSec > 0 ? (p.currentSec / p.durationSec) * 100 : 0;
  const buffPct = p.durationSec > 0 ? (p.bufferedSec / p.durationSec) * 100 : 0;

  const onScrub = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    const next = Number(target.value);
    if (Number.isFinite(next)) seekAbsolute(next);
  };

  const playLabel = p.playing ? 'Pause' : 'Play';

  // Chapter-boundary flags for skip-prev / skip-next. We read `books`
  // here so the component re-renders when the canon list arrives, and
  // route so prev/next flip at the right moments.
  const bookList = books.value;
  const here = route.value;
  const { hasPrev, hasNext } = computeNeighbours(bookList, here.book, here.chapter);

  return (
    <>
      <footer class="player-dock" aria-label="Audio player">
        <div class="player-progress">
          <div class="player-progress-track" aria-hidden="true">
            <div class="player-progress-buffered" style={{ width: `${buffPct}%` }} />
            <div class="player-progress-played" style={{ width: `${pct}%` }} />
          </div>
          <input
            class="player-progress-input"
            type="range"
            min={0}
            max={Math.max(1, p.durationSec)}
            step={0.1}
            value={p.currentSec}
            onInput={onScrub}
            disabled={disabled}
            aria-label="Seek"
          />
        </div>

        <div class="player-bar" role="toolbar" aria-label="Player controls">
          <button
            type="button"
            class="btn util"
            aria-haspopup="dialog"
            aria-expanded={settingsOpen}
            aria-label="Playback settings"
            onClick={() => setSettingsOpen(true)}
          >
            <GearIcon size={24} />
          </button>

          <button
            type="button"
            class="btn skip"
            onClick={() => void gotoPrevChapter()}
            disabled={!hasPrev}
            aria-label="Previous chapter"
            title="Previous chapter"
          >
            <SkipPrevIcon size={24} />
          </button>

          <button
            type="button"
            class="btn scrub bck"
            onClick={() => void gotoPrevVerse()}
            disabled={disabled}
            aria-label="Previous verse"
            title="Previous verse"
          >
            <ScrubBackIcon size={24} />
          </button>

          <button
            type="button"
            class={`btn play${p.playing ? ' playing' : ''}`}
            onClick={() => void togglePlay()}
            disabled={disabled}
            aria-label={playLabel}
            aria-pressed={p.playing}
          >
            {p.playing ? <PauseIcon size={24} /> : <PlayIcon size={24} />}
          </button>

          <button
            type="button"
            class="btn scrub fwd"
            onClick={() => void gotoNextVerse()}
            disabled={disabled}
            aria-label="Next verse"
            title="Next verse"
          >
            <ScrubFwdIcon size={24} />
          </button>

          <button
            type="button"
            class="btn skip"
            onClick={() => void gotoNextChapter()}
            disabled={!hasNext}
            aria-label="Next chapter"
            title="Next chapter"
          >
            <SkipNextIcon size={24} />
          </button>

          <button
            type="button"
            class={`btn util util-right player-ai${askOpen.value ? ' is-on' : ''}`}
            aria-label="Open AI chat"
            aria-pressed={askOpen.value}
            onClick={() => (askOpen.value = !askOpen.value)}
            title="Ask about this chapter"
          >
            <AiSparkleIcon size={24} />
          </button>
        </div>

        {resumeAvailable.value > 0 && (status === 'ready' || status === 'demo') ? (
          <div class="player-resume" role="status">
            <button
              type="button"
              class="player-resume-btn"
              onClick={() => void resumeFromSaved()}
            >
              Continue at {fmt(resumeAvailable.value)}
            </button>
            <button
              type="button"
              class="player-resume-dismiss"
              aria-label="Dismiss resume position"
              onClick={dismissResume}
            >
              ×
            </button>
          </div>
        ) : null}
        {status === 'unavailable' ? (
          <div class="player-status" role="status">Audio not available for this chapter</div>
        ) : null}
        {status === 'demo' ? (
          <div class="player-status" role="status">Karaoke demo · audio unavailable</div>
        ) : null}
        {status === 'error' ? (
          <div class="player-status player-status-error" role="alert">
            <span>{chapterError.value ?? 'Failed to load chapter.'}</span>
            <button type="button" class="player-status-retry" onClick={retryLoad}>
              Retry
            </button>
          </div>
        ) : null}
        {status === 'loading' ? (
          <div class="player-status" role="status">Loading…</div>
        ) : null}
        <SleepStatusBadge />
        {biblePickerOpen ? <BiblePicker onClose={() => setBiblePickerOpen(false)} /> : null}
        {comparePickerOpen ? (
          <BiblePicker
            mode="compare"
            onClose={() => setComparePickerOpen(false)}
          />
        ) : null}
      </footer>

      {settingsOpen ? (
        <SettingsSheet
          onClose={() => setSettingsOpen(false)}
          onBiblePick={() => { setSettingsOpen(false); setBiblePickerOpen(true); }}
          onComparePick={() => { setSettingsOpen(false); setComparePickerOpen(true); }}
          onOpenBadges={() => { setSettingsOpen(false); setBadgeInventoryOpen(true); }}
          onSaveOffline={() => { setSettingsOpen(false); openOfflinePrompt(); }}
        />
      ) : null}
      {badgeInventoryOpen ? <BadgeInventory onClose={() => setBadgeInventoryOpen(false)} /> : null}
      <OfflinePrompt />
      <DownloadProgressBar />
    </>
  );
}

type SettingsSheetProps = {
  onClose: () => void;
  onBiblePick: () => void;
  onComparePick: () => void;
  onOpenBadges: () => void;
  onSaveOffline: () => void;
};

function SettingsSheet({ onClose, onBiblePick, onComparePick, onOpenBadges, onSaveOffline }: SettingsSheetProps) {
  const settings = playerSettings.value;
  const dramatized = settings.audioStyle === 'dramatized';
  const prefs = readingPrefs.value;

  const onScrim = (e: MouseEvent) => {
    if ((e.target as HTMLElement) === (e.currentTarget as HTMLElement)) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div class="modal-scrim" onClick={onScrim}>
      <div class="modal-sheet">
        <div class="modal-sheet-grabber" />
        <div class="modal-sheet-header">
          <span class="modal-sheet-title">Reader Settings</span>
          <button type="button" class="modal-sheet-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="modal-sheet-body">
          {/* Speed row — inline horizontal slider; changing speed no longer
              expands a panel or reflows the menu below it. */}
          <SpeedRow rate={settings.rate} />

          {/* Audio */}
          <div class="settings-section-label">Audio</div>
          <div class="seg-ctrl">
            <button type="button" class={`seg-ctrl-opt${dramatized ? ' is-active' : ''}`} onClick={() => setAudioStyle('dramatized')}>Dramatized</button>
            <button type="button" class={`seg-ctrl-opt${!dramatized ? ' is-active' : ''}`} onClick={() => setAudioStyle('plain')}>Plain</button>
          </div>

          {/* Bible */}
          <div class="settings-section-label">Bible</div>
          <button type="button" class="settings-row-btn" onClick={onBiblePick}>
            <span class="s-label">{currentBible.value.displayName}</span>
            <span class="s-value">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </span>
          </button>
          <button type="button" class="settings-row-btn" onClick={compareBible.value ? () => setCompareBible(null) : onComparePick}>
            <span class="s-label" style={{ color: compareBible.value ? 'var(--ink)' : 'var(--muted)' }}>
              {compareBible.value ? `Comparing: ${compareBible.value.displayName}` : 'Compare with…'}
            </span>
            <span class="s-value">
              {compareBible.value ? (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <polyline points="3 8.5 7 12 13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              )}
            </span>
          </button>

          {/* Appearance */}
          <div class="settings-section-label">Appearance</div>
          <ThemeGridNew />

          {/* Reading size */}
          <div class="settings-section-label">Reading size</div>
          <div class="seg-ctrl seg-ctrl--size">
            {FONT_SIZES.map((s) => (
              <button key={s} type="button" class={`seg-ctrl-opt${s === prefs.fontSize ? ' is-active' : ''}`} onClick={() => setFontSize(s)}>
                {FONT_SIZE_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Spacing */}
          <div class="settings-section-label" style={{ marginTop: 14 }}>Line spacing</div>
          <div class="seg-ctrl">
            {LINE_SPACINGS.map((s) => (
              <button key={s} type="button" class={`seg-ctrl-opt${s === prefs.lineSpacing ? ' is-active' : ''}`} onClick={() => setLineSpacing(s)}>
                {LINE_SPACING_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Literacy mode */}
          <div class="settings-section-label" style={{ marginTop: 14 }}>Literacy mode</div>
          <div class="seg-ctrl">
            <button type="button" class={`seg-ctrl-opt${!prefs.literacyMode ? ' is-active' : ''}`} onClick={() => setLiteracyMode(false)}>Off</button>
            <button type="button" class={`seg-ctrl-opt${prefs.literacyMode ? ' is-active' : ''}`} onClick={() => setLiteracyMode(true)}>On</button>
          </div>

          {/* View */}
          <div class="settings-section-label" style={{ marginTop: 14 }}>View</div>
          <div class="seg-ctrl">
            <button type="button" class={`seg-ctrl-opt${!prefs.paragraphMode ? ' is-active' : ''}`} onClick={() => setParagraphMode(false)}>Verse</button>
            <button type="button" class={`seg-ctrl-opt${prefs.paragraphMode ? ' is-active' : ''}`} onClick={() => setParagraphMode(true)}>Paragraph</button>
          </div>

          {/* Achievements */}
          <div class="settings-section-label">Achievements</div>
          <AchievementsRow onOpenBadges={onOpenBadges} />

          {/* Verse of the day */}
          <div class="settings-section-label">Verse of the day</div>
          <NotificationsRowNew />

          {/* Sleep timer */}
          <div class="settings-section-label">Sleep timer</div>
          <SleepRowNew />

          {/* Offline */}
          {(window as { __SECURE_MODE__?: string }).__SECURE_MODE__ === 'restricted' && (
            <>
              <div class="settings-section-label">Offline</div>
              <button type="button" class="settings-row-btn" onClick={onSaveOffline}>
                <span class="s-label">Save for offline</span>
                <span class="s-value">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline playback-speed control. The five rates the app supports live in
 * PLAYBACK_RATES (= [2, 1.75, 1.5, 1.25, 1]); we present them low→high on a
 * horizontal slider that uses the row's existing horizontal headroom. Changing
 * speed mutates only the value + thumb position — the surrounding menu never
 * reflows (replaces the old vertical disclosure / sub-sheet).
 */
const SPEED_STEPS = PLAYBACK_RATES.slice().reverse(); // [1, 1.25, 1.5, 1.75, 2]

function SpeedRow({ rate }: { rate: number }) {
  const maxIdx = SPEED_STEPS.length - 1;
  const activeIdx = Math.max(0, SPEED_STEPS.indexOf(rate as (typeof SPEED_STEPS)[number]));
  const onInput = (e: Event) => {
    const idx = Number((e.currentTarget as HTMLInputElement).value);
    const next = SPEED_STEPS[idx];
    if (typeof next === 'number') setRate(next);
  };
  return (
    <div class="speed-row">
      <span class="s-label">Speed</span>
      <input
        class="speed-slider"
        type="range"
        min={0}
        max={maxIdx}
        step={1}
        value={activeIdx}
        onInput={onInput}
        aria-label="Playback speed"
        aria-valuetext={fmtRate(rate)}
      />
      <span class="speed-row-value">{fmtRate(rate)}</span>
    </div>
  );
}

function ThemeGridNew() {
  const current = colorProfile.value;
  return (
    <div class="theme-grid-new" role="group" aria-label="Color theme">
      {COLOR_PROFILES.map((p: { id: string; label: string; bg: string; inkColor: string }) => (
        <button
          key={p.id}
          type="button"
          class={`theme-card-new${current === p.id ? ' is-active' : ''}`}
          aria-pressed={current === p.id}
          aria-label={p.label}
          onClick={() => setColorProfile(p.id as Parameters<typeof setColorProfile>[0])}
        >
          <div class="theme-swatch-new" style={{ background: p.bg }}>
            <div class="ts-l1" style={{ background: p.inkColor, opacity: .85 }} />
            <div class="ts-l2" style={{ background: p.inkColor, opacity: .55 }} />
            <div class="ts-l3" style={{ background: p.inkColor, opacity: .70 }} />
          </div>
          <div class="theme-label-new">{p.label}</div>
        </button>
      ))}
    </div>
  );
}

function SleepStatusBadge() {
  const t = sleepTimer.value;
  if (!t) return null;
  const label =
    t.kind === 'minutes'
      ? `Sleep in ${fmtCountdown(sleepRemainingSec.value)}`
      : 'Stops at end of chapter';
  return (
    <div class="player-sleep-badge" role="status">
      <span aria-hidden="true">🌙</span>
      <span>{label}</span>
      <button
        type="button"
        class="player-sleep-cancel"
        onClick={cancelSleepTimer}
        aria-label="Cancel sleep timer"
      >
        ×
      </button>
    </div>
  );
}

function NotificationsRowNew() {
  const prefs = notificationPrefs.value;
  const onToggle = async (): Promise<void> => {
    if (prefs.enabled) {
      setNotificationsEnabled(false);
      return;
    }
    const ok = await requestPermissionAndEnable();
    if (!ok) {
      // Permission denied — leave disabled.
    }
  };
  return (
    <div class="player-reading-row">
      <div class="player-reading-line">
        <span class="player-reading-line-label">Daily</span>
        <button
          type="button"
          class={`player-sleep-chip${prefs.enabled ? ' is-active' : ''}`}
          style={{ flex: 1 }}
          onClick={() => void onToggle()}
        >
          {prefs.enabled ? 'On' : 'Off'}
        </button>
      </div>
      {prefs.enabled ? (
        <div class="player-reading-line">
          <span class="player-reading-line-label">Time</span>
          <select
            class="player-notif-time"
            value={prefs.hour}
            onChange={(e) => setNotificationHour(Number((e.currentTarget as HTMLSelectElement).value))}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
}

function SleepRowNew() {
  const t = sleepTimer.value;
  const activeMinutes = t?.kind === 'minutes' ? t.minutes : null;
  const endOfChapter = t?.kind === 'end-of-chapter';

  return (
    <div class="player-sleep-row">
      <div class="player-segment" role="group">
        <button
          type="button"
          class={`player-segment-btn${t == null ? ' is-active' : ''}`}
          aria-pressed={t == null}
          onClick={cancelSleepTimer}
        >
          Off
        </button>
        {SLEEP_PRESETS_MINUTES.map((m) => (
          <button
            key={m}
            type="button"
            class={`player-segment-btn${activeMinutes === m ? ' is-active' : ''}`}
            aria-pressed={activeMinutes === m}
            onClick={() => startSleepTimer(m)}
          >
            {m}m
          </button>
        ))}
      </div>
      <button
        type="button"
        class={`player-settings-opt${endOfChapter ? ' is-active' : ''}`}
        onClick={() => (endOfChapter ? cancelSleepTimer() : scheduleEndOfChapter())}
      >
        <span class="player-settings-check" aria-hidden="true">{endOfChapter ? '✓' : ''}</span>
        End of chapter
      </button>
    </div>
  );
}

function AchievementsRow({ onOpenBadges }: { onOpenBadges: () => void }) {
  const earned = earnedBadges.value;
  const count = BADGE_CATALOG.filter((b) => !!earned[b.id]).length;
  const tokens = freezeTokens.value;
  return (
    <div class="achievements-row">
      <button type="button" class="settings-row-btn" onClick={onOpenBadges}>
        <span class="s-label">Badges</span>
        <span class="s-value">
          {count}/{BADGE_CATALOG.length}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </span>
      </button>
      {tokens > 0 ? (
        <button type="button" class="settings-row-btn" onClick={consumeFreezeToken}>
          <span class="s-label">Streak freeze</span>
          <span class="s-value" style={{ color: 'var(--muted)' }}>
            {tokens} token{tokens !== 1 ? 's' : ''} · Use
          </span>
        </button>
      ) : null}
    </div>
  );
}

type Neighbours = { hasPrev: boolean; hasNext: boolean };

function computeNeighbours(
  list: ReadonlyArray<{ bookId: string; chapters: number[] }>,
  book: string,
  chapter: number,
): Neighbours {
  // Until the canon list arrives, leave both enabled so the buttons
  // don't flicker disabled on first paint; the controller's gotoPrev/
  // gotoNextChapter() are both no-ops when books.value is empty.
  if (list.length === 0) return { hasPrev: true, hasNext: true };
  const idx = list.findIndex((b) => b.bookId === book.toUpperCase());
  if (idx === -1) return { hasPrev: false, hasNext: false };
  const cur = list[idx];
  if (!cur) return { hasPrev: false, hasNext: false };
  const hasPrevInBook = cur.chapters.some((c) => c < chapter);
  const hasNextInBook = cur.chapters.some((c) => c > chapter);
  const hasPrevBook = idx > 0 && (list[idx - 1]?.chapters.length ?? 0) > 0;
  const hasNextBook =
    idx < list.length - 1 && (list[idx + 1]?.chapters.length ?? 0) > 0;
  return {
    hasPrev: hasPrevInBook || hasPrevBook,
    hasNext: hasNextInBook || hasNextBook,
  };
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function fmtRate(rate: number): string {
  return rate === 1 ? '1×' : `${rate}×`;
}

function fmtCountdown(totalSec: number): string {
  if (totalSec < 0) return '—';
  const m = Math.floor(totalSec / 60);
  const s = (totalSec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
